import React, { useState, useEffect } from "react";
import SubscriptionService from "../services/SubscriptionService";
import DataTable from "../components/DataTable";
import Loader from "../components/Loader";
import SubscriptionAnalytics from "../components/SubscriptionAnalytics";
import UnsubscribedDrivers from "../components/UnsubscribedDrivers";
import SubscriptionDetailModal from "../components/SubscriptionDetailModal";
import { toast } from "react-toastify";
import { FiList, FiBarChart, FiUsers, FiEye } from "react-icons/fi";

const SubscriptionManagement = () => {
  const [activeTab, setActiveTab] = useState("subscriptions");
  const [subscriptions, setSubscriptions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: "",
    paymentStatus: "",
    search: "",
    startDate: "",
    endDate: "",
  });
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);

  useEffect(() => {
    loadData();
  }, [currentPage, filters]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load analytics and subscriptions in parallel
      const [analyticsData, subscriptionsData] = await Promise.all([
        SubscriptionService.getAnalytics(),
        SubscriptionService.getAllSubscriptions({
          page: currentPage,
          limit: 20,
          ...filters,
        }),
      ]);

      setAnalytics(analyticsData?.analytics || {});

      // Ensure subscriptions is always an array and has proper structure
      const subscriptions = subscriptionsData?.subscriptions || [];
      const processedSubscriptions = subscriptions.map((subscription) => ({
        ...subscription,
        driver: subscription.driver || {
          fullName: "Unknown",
          phone: "N/A",
          email: "",
        },
      }));

      setSubscriptions(processedSubscriptions);
      setTotalPages(subscriptionsData?.pagination?.totalPages || 1);
    } catch (error) {
      console.error("Error loading subscription data:", error);
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setCurrentPage(1);
  };

  const handleBulkExpire = async () => {
    if (selectedSubscriptions.length === 0) {
      toast.warning("Please select subscriptions to expire");
      return;
    }

    const reason = prompt("Enter reason for bulk expiration:");
    if (!reason) return;

    try {
      await SubscriptionService.bulkExpireSubscriptions(
        selectedSubscriptions,
        reason
      );
      setSelectedSubscriptions([]);
      loadData();
    } catch (error) {
      console.error("Error bulk expiring subscriptions:", error);
    }
  };

  const handleActivateSubscription = async (subscriptionId) => {
    const duration = prompt("Enter duration in days (default: 30):", "30");
    if (!duration) return;

    try {
      await SubscriptionService.activateSubscription(
        subscriptionId,
        parseInt(duration)
      );
      loadData();
    } catch (error) {
      console.error("Error activating subscription:", error);
    }
  };

  const handleExpireSubscription = async (subscriptionId) => {
    const reason = prompt("Enter reason for expiration:");
    if (!reason) return;

    try {
      await SubscriptionService.expireSubscription(subscriptionId, reason);
      loadData();
    } catch (error) {
      console.error("Error expiring subscription:", error);
    }
  };

  const handleSendReminder = async (subscriptionId) => {
    setSelectedSubscription(subscriptionId);
    setShowReminderModal(true);
  };

  const handleViewDetails = (subscriptionId) => {
    setSelectedSubscriptionId(subscriptionId);
    setShowDetailModal(true);
  };

  const sendReminderMessage = async (message) => {
    if (!message.trim()) {
      toast.warning("Please enter a reminder message");
      return;
    }

    try {
      await SubscriptionService.sendRenewalReminder(
        selectedSubscription,
        message
      );
      setShowReminderModal(false);
      setSelectedSubscription(null);
    } catch (error) {
      console.error("Error sending reminder:", error);
    }
  };

  const handleExport = async () => {
    try {
      await SubscriptionService.exportSubscriptions(filters);
    } catch (error) {
      console.error("Error exporting data:", error);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      ACTIVE: "bg-green-100 text-green-800",
      EXPIRED: "bg-red-100 text-red-800",
      CANCELLED: "bg-gray-100 text-gray-800",
      PENDING: "bg-yellow-100 text-yellow-800",
    };

    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${
          statusColors[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const statusColors = {
      PAID: "bg-green-100 text-green-800",
      PENDING: "bg-yellow-100 text-yellow-800",
      FAILED: "bg-red-100 text-red-800",
      REFUNDED: "bg-blue-100 text-blue-800",
    };

    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${
          statusColors[paymentStatus] || "bg-gray-100 text-gray-800"
        }`}
      >
        {paymentStatus}
      </span>
    );
  };

  const columns = [
    {
      Header: "Driver",
      accessor: "driver",
      Cell: ({ value: driver }) => (
        <div>
          <div className="font-medium text-gray-900">
            {driver?.fullName || "N/A"}
          </div>
          <div className="text-sm text-gray-500">{driver?.phone || "N/A"}</div>
        </div>
      ),
    },
    {
      Header: "Plan",
      accessor: "planName",
    },
    {
      Header: "Amount",
      accessor: "amount",
      Cell: ({ value: amount }) => `₹${amount}`,
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value: status }) => getStatusBadge(status),
    },
    {
      Header: "Payment Status",
      accessor: "paymentStatus",
      Cell: ({ value: paymentStatus }) => getPaymentStatusBadge(paymentStatus),
    },
    {
      Header: "Start Date",
      accessor: "subscriptionStartDate",
      Cell: ({ value: date }) => new Date(date).toLocaleDateString(),
    },
    {
      Header: "End Date",
      accessor: "subscriptionEndDate",
      Cell: ({ value: date }) => new Date(date).toLocaleDateString(),
    },
    {
      header: "Rides",
      accessor: "ridesCompleted",
      render: (rides) => rides || 0,
    },
    {
      header: "Actions",
      accessor: "_id",
      render: (id, subscription) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewDetails(id)}
            className="text-gray-600 hover:text-gray-900 text-sm flex items-center"
          >
            <FiEye className="mr-1" size={14} />
            View
          </button>
          {subscription.status === "EXPIRED" ||
          subscription.status === "CANCELLED" ? (
            <button
              onClick={() => handleActivateSubscription(id)}
              className="text-green-600 hover:text-green-900 text-sm"
            >
              Activate
            </button>
          ) : (
            <button
              onClick={() => handleExpireSubscription(id)}
              className="text-red-600 hover:text-red-900 text-sm"
            >
              Expire
            </button>
          )}
          <button
            onClick={() => handleSendReminder(id)}
            className="text-blue-600 hover:text-blue-900 text-sm"
          >
            Remind
          </button>
        </div>
      ),
    },
  ];

  if (loading && !subscriptions.length) {
    return <Loader />;
  }

  const tabs = [
    { id: "subscriptions", label: "Subscriptions", icon: FiList },
    { id: "analytics", label: "Analytics", icon: FiBarChart },
    { id: "unsubscribed", label: "Unsubscribed Drivers", icon: FiUsers },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Icon className="mr-2" size={16} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "analytics" && <SubscriptionAnalytics />}

      {activeTab === "unsubscribed" && <UnsubscribedDrivers />}

      {activeTab === "subscriptions" && (
        <div className="space-y-6">
          {/* Analytics Cards */}
          {analytics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-sm font-medium text-gray-500">
                  Total Subscriptions
                </div>
                <div className="mt-2 text-3xl font-bold text-gray-900">
                  {analytics.totalSubscriptions}
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-sm font-medium text-gray-500">
                  Active Subscriptions
                </div>
                <div className="mt-2 text-3xl font-bold text-green-600">
                  {analytics.activeSubscriptions}
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-sm font-medium text-gray-500">
                  Total Revenue
                </div>
                <div className="mt-2 text-3xl font-bold text-blue-600">
                  ₹{analytics.totalRevenue}
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-sm font-medium text-gray-500">Expired</div>
                <div className="mt-2 text-3xl font-bold text-red-600">
                  {analytics.statusBreakdown.EXPIRED || 0}
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">All Statuses</option>
                  <option value="ACTIVE">Active</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Status
                </label>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) =>
                    handleFilterChange("paymentStatus", e.target.value)
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">All Payment Statuses</option>
                  <option value="PAID">Paid</option>
                  <option value="PENDING">Pending</option>
                  <option value="FAILED">Failed</option>
                  <option value="REFUNDED">Refunded</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  placeholder="Search by driver name, phone..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>
          </div>

          {/* Actions Bar */}
          <div className="bg-white p-4 rounded-lg shadow flex justify-between items-center">
            <div className="flex space-x-4">
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Bulk Actions
              </button>
              <button
                onClick={handleExport}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
              >
                Export CSV
              </button>
            </div>
            {selectedSubscriptions.length > 0 && (
              <div className="text-sm text-gray-600">
                {selectedSubscriptions.length} selected
              </div>
            )}
          </div>

          {/* Bulk Actions */}
          {showBulkActions && (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <div className="flex space-x-4">
                <button
                  onClick={handleBulkExpire}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                  disabled={selectedSubscriptions.length === 0}
                >
                  Bulk Expire ({selectedSubscriptions.length})
                </button>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="bg-white rounded-lg shadow">
            <DataTable
              columns={columns}
              data={subscriptions}
              loading={loading}
              selectable={showBulkActions}
              selectedItems={selectedSubscriptions}
              onSelectionChange={setSelectedSubscriptions}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Reminder Modal */}
          {showReminderModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg w-96">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Send Renewal Reminder
                </h3>
                <textarea
                  placeholder="Enter reminder message..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 h-32 resize-none"
                  defaultValue="Your subscription expires soon. Please renew to continue earning!"
                />
                <div className="flex space-x-4 mt-4">
                  <button
                    onClick={(e) => {
                      const message =
                        e.target.parentElement.previousElementSibling.value;
                      sendReminderMessage(message);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    Send Reminder
                  </button>
                  <button
                    onClick={() => {
                      setShowReminderModal(false);
                      setSelectedSubscription(null);
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Subscription Detail Modal */}
          {showDetailModal && (
            <SubscriptionDetailModal
              subscriptionId={selectedSubscriptionId}
              isOpen={showDetailModal}
              onClose={() => {
                setShowDetailModal(false);
                setSelectedSubscriptionId(null);
              }}
              onUpdate={loadData}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
